import { FC, useState, useEffect, useMemo } from 'react';
import { SlidersHorizontal, ChevronDown, List, LayoutGrid, Map, ArrowUp, ArrowDown } from 'lucide-react';
import { Property, PropertyStatus } from '@/types/index';
import { getStatusLabel } from '@utils/status-formatter';
import { computeFlipStatus, FlipStatus } from '@/utils/flipViability';

const STATUS_CHIP_COLORS: Record<PropertyStatus, string> = {
  FIRST_SELECTION: 'bg-blue-600 text-white border-blue-600',
  OPPORTUNITY:     'bg-green-600 text-white border-green-600',
  UNDER_ANALYSIS:  'bg-amber-500 text-white border-amber-500',
  PURCHASED:       'bg-emerald-600 text-white border-emerald-600',
  WITHDRAWN:       'bg-red-500 text-white border-red-500',
  REJECTED:        'bg-red-600 text-white border-red-600',
};

const STATUS_ORDER: PropertyStatus[] = [
  PropertyStatus.FIRST_SELECTION, PropertyStatus.OPPORTUNITY,
  PropertyStatus.UNDER_ANALYSIS, PropertyStatus.PURCHASED,
  PropertyStatus.WITHDRAWN, PropertyStatus.REJECTED,
];

export type ViewMode = 'table' | 'cards' | 'map';

type SortField = 'none' | 'city' | 'address' | 'askingPrice' | 'market' | 'area' | 'pricePerM2' | 'yield' | 'daysOnMarket' | 'status';
type SortDir = 'asc' | 'desc';

const SORT_OPTIONS: { value: SortField; label: string }[] = [
  { value: 'none',       label: 'Trier par…' },
  { value: 'city',       label: 'Ville' },
  { value: 'address',    label: 'Adresse' },
  { value: 'askingPrice',label: 'Prix de vente' },
  { value: 'market',     label: 'vs Marché' },
  { value: 'area',       label: 'Surface' },
  { value: 'pricePerM2', label: 'Prix/m²' },
  { value: 'yield',      label: 'Rendement' },
  { value: 'daysOnMarket', label: 'Durée annonce' },
  { value: 'status',     label: 'Statut' },
];

interface PropertyFilterBarProps {
  properties: Property[];
  onFilterChange: (filtered: Property[]) => void;
  defaultStatuses?: PropertyStatus[];
  showOffMarket?: boolean;
  onToggleOffMarket?: () => void;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
}

const formatPrice = (value: number) =>
  `CHF ${value.toLocaleString('fr-CH')}`;

const selectClass =
  'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer';

function daysOnMarket(p: Property): number | null {
  if (!p.firstSeenAt) return null;
  return (Date.now() - new Date(p.firstSeenAt).getTime()) / 86400000;
}

export const PropertyFilterBar: FC<PropertyFilterBarProps> = ({
  properties, onFilterChange, defaultStatuses, showOffMarket, onToggleOffMarket, viewMode, onViewModeChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredCount, setFilteredCount] = useState(properties.length);
  const [citySearch, setCitySearch] = useState('');
  const [selectedCantons, setSelectedCantons] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<PropertyStatus[]>(
    defaultStatuses ?? []
  );
  const [roomsMin, setRoomsMin] = useState<number | null>(null);
  const [roomsMax, setRoomsMax] = useState<number | null>(null);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 0]);
  const [priceInitialized, setPriceInitialized] = useState(false);
  const [sortBy, setSortBy] = useState<SortField>('none');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [flagRented, setFlagRented] = useState<boolean | null>(null);
  const [flagRenovationLevel, setFlagRenovationLevel] = useState<'none' | 'minor' | 'major' | null>(null);
  const [flagMixed, setFlagMixed] = useState<boolean | null>(null);
  const [flagAddressIncomplete, setFlagAddressIncomplete] = useState(false);
  const [flagStrategyLocatif, setFlagStrategyLocatif] = useState<boolean | null>(null);
  const [flagStrategyFlip, setFlagStrategyFlip] = useState<boolean | null>(null);
  const [flagFlipViability, setFlagFlipViability] = useState<FlipStatus | null>(null);

  const availableCantons = useMemo(() => {
    const cantons = properties.map(p => p.canton).filter((c): c is string => !!c);
    return [...new Set(cantons)].sort();
  }, [properties]);

  const availableStatuses = useMemo(() => {
    const statuses = properties.map(p => p.status).filter((s): s is PropertyStatus => !!s);
    const unique = [...new Set(statuses)];
    return STATUS_ORDER.filter(s => unique.includes(s));
  }, [properties]);

  const availableRooms = useMemo(() => {
    const rooms = properties.map(p => p.rooms).filter((r): r is number => r != null && r > 0);
    return [...new Set(rooms)].sort((a, b) => a - b);
  }, [properties]);

  const [globalMin, globalMax] = useMemo(() => {
    const prices = properties.map(p => p.askingPrice).filter((p): p is number => p != null && p > 0);
    if (!prices.length) return [0, 5000000];
    return [Math.min(...prices), Math.max(...prices)];
  }, [properties]);

  useEffect(() => {
    if (properties.length > 0 && !priceInitialized) {
      setPriceRange([globalMin, globalMax]);
      setPriceInitialized(true);
    }
  }, [properties, globalMin, globalMax, priceInitialized]);

  useEffect(() => {
    if (priceInitialized) setPriceRange([globalMin, globalMax]);
  }, [globalMin, globalMax]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (citySearch.trim()) count++;
    if (selectedCantons.length) count++;
    if (selectedStatuses.length) count++;
    if (roomsMin !== null || roomsMax !== null) count++;
    if (priceInitialized && (priceRange[0] > globalMin || priceRange[1] < globalMax)) count++;
    if (flagRented !== null) count++;
    if (flagRenovationLevel !== null) count++;
    if (flagMixed !== null) count++;
    if (flagAddressIncomplete) count++;
    if (flagStrategyLocatif !== null) count++;
    if (flagStrategyFlip !== null) count++;
    if (flagFlipViability !== null) count++;
    if (showOffMarket) count++;
    return count;
  }, [citySearch, selectedCantons, selectedStatuses, roomsMin, roomsMax, priceRange, globalMin, globalMax, priceInitialized, flagRented, flagRenovationLevel, flagMixed, flagAddressIncomplete, flagStrategyLocatif, flagStrategyFlip, flagFlipViability, showOffMarket]);

  const getSortValue = (p: Property): number | string => {
    switch (sortBy) {
      case 'city':       return p.city ?? '';
      case 'address':    return p.address ?? '';
      case 'askingPrice': return p.askingPrice ?? Infinity;
      case 'market': {
        if (!p.askingPrice || !p.estimatedPrice) return Infinity;
        return ((p.askingPrice - p.estimatedPrice) / p.estimatedPrice) * 100;
      }
      case 'area':       return p.area ?? Infinity;
      case 'pricePerM2': return p.pricePerM2 ?? Infinity;
      case 'yield':      return (p.estimatedYield ?? p.yieldRate) ?? -Infinity;
      case 'daysOnMarket': return daysOnMarket(p) ?? Infinity;
      case 'status':     return p.status ? STATUS_ORDER.indexOf(p.status) : 99;
      default:           return 0;
    }
  };

  useEffect(() => {
    let filtered = properties;

    if (citySearch.trim()) {
      const q = citySearch.trim().toLowerCase();
      filtered = filtered.filter(p => p.city?.toLowerCase().includes(q));
    }

    if (selectedCantons.length) {
      filtered = filtered.filter(p => p.canton && selectedCantons.includes(p.canton));
    }

    if (selectedStatuses.length) {
      filtered = filtered.filter(p => p.status && selectedStatuses.includes(p.status));
    }

    if (roomsMin !== null) {
      filtered = filtered.filter(p => p.rooms != null && p.rooms >= roomsMin);
    }
    if (roomsMax !== null) {
      filtered = filtered.filter(p => p.rooms != null && p.rooms <= roomsMax);
    }

    if (priceInitialized && (priceRange[0] > globalMin || priceRange[1] < globalMax)) {
      filtered = filtered.filter(p => {
        if (p.askingPrice == null) return priceRange[0] === globalMin;
        return p.askingPrice >= priceRange[0] && p.askingPrice <= priceRange[1];
      });
    }

    if (flagRented !== null) {
      filtered = filtered.filter(p => p.isRented === flagRented);
    }
    if (flagRenovationLevel !== null) {
      filtered = filtered.filter(p => p.renovationLevel === flagRenovationLevel);
    }
    if (flagMixed !== null) {
      filtered = filtered.filter(p => p.isMixedUse === flagMixed);
    }
    if (flagStrategyLocatif !== null) {
      filtered = filtered.filter(p => p.isStrategyLocatif === flagStrategyLocatif);
    }
    if (flagStrategyFlip !== null) {
      filtered = filtered.filter(p => p.isStrategyFlip === flagStrategyFlip);
    }
    if (flagFlipViability !== null) {
      filtered = filtered.filter(p => computeFlipStatus(p) === flagFlipViability);
    }
    if (flagAddressIncomplete) {
      filtered = filtered.filter(p => p.isAddressComplete === false);
    }

    if (sortBy !== 'none') {
      filtered = [...filtered].sort((a, b) => {
        const va = getSortValue(a);
        const vb = getSortValue(b);
        const cmp = typeof va === 'string' && typeof vb === 'string'
          ? va.localeCompare(vb, 'fr')
          : (va as number) - (vb as number);
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }

    setFilteredCount(filtered.length);
    onFilterChange(filtered);
  }, [citySearch, selectedCantons, selectedStatuses, roomsMin, roomsMax, priceRange, properties, sortBy, sortDir, flagRented, flagRenovationLevel, flagMixed, flagAddressIncomplete, flagStrategyLocatif, flagStrategyFlip, flagFlipViability]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCanton = (canton: string) => {
    setSelectedCantons(prev =>
      prev.includes(canton) ? prev.filter(c => c !== canton) : [...prev, canton]
    );
  };

  const toggleStatus = (status: PropertyStatus) => {
    setSelectedStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const handleReset = () => {
    setCitySearch('');
    setSelectedCantons([]);
    setSelectedStatuses([]);
    setRoomsMin(null);
    setRoomsMax(null);
    setPriceRange([globalMin, globalMax]);
    setSortBy('none');
    setFlagRented(null);
    setFlagRenovationLevel(null);
    setFlagMixed(null);
    setFlagAddressIncomplete(false);
    setFlagStrategyLocatif(null);
    setFlagStrategyFlip(null);
    setFlagFlipViability(null);
  };

  const formatRooms = (r: number) => r % 1 === 0 ? String(r) : r.toFixed(1);

  const chipClass = (active: boolean) =>
    `px-3 py-1 rounded-full text-sm font-medium cursor-pointer border transition-colors select-none ${
      active
        ? 'bg-blue-600 text-white border-blue-600'
        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-blue-400'
    }`;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
      {/* Toggle bar */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 flex-wrap">
        <button
          onClick={() => setIsOpen(o => !o)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          <SlidersHorizontal className="w-4 h-4" />
          Filtres
          {activeFilterCount > 0 && (
            <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-semibold">
              {activeFilterCount}
            </span>
          )}
          <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 font-normal">
            {filteredCount} annonce{filteredCount !== 1 ? 's' : ''}
          </span>
        </button>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Sort controls */}
          <div className="flex items-center gap-1">
            <div className="relative">
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value as SortField)}
                className="pl-3 pr-7 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                <ChevronDown className="w-3 h-3 text-gray-400" />
              </div>
            </div>
            {sortBy !== 'none' && (
              <button
                onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                title={sortDir === 'asc' ? 'Croissant — cliquer pour décroissant' : 'Décroissant — cliquer pour croissant'}
                className="px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
              >
                {sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
              </button>
            )}
          </div>

          {/* Vue toggle */}
          {onViewModeChange && (
            <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              <button onClick={() => onViewModeChange('table')} title="Tableau"
                className={`px-2.5 py-1.5 transition-colors ${viewMode === 'table' ? 'bg-primary-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >
                <List className="w-4 h-4" />
              </button>
              <button onClick={() => onViewModeChange('cards')} title="Cards"
                className={`px-2.5 py-1.5 transition-colors border-l border-gray-200 dark:border-gray-600 ${viewMode === 'cards' ? 'bg-primary-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button onClick={() => onViewModeChange('map')} title="Carte"
                className={`px-2.5 py-1.5 transition-colors border-l border-gray-200 dark:border-gray-600 ${viewMode === 'map' ? 'bg-primary-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >
                <Map className="w-4 h-4" />
              </button>
            </div>
          )}


          {(activeFilterCount > 0 || sortBy !== 'none') && (
            <button
              onClick={handleReset}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
            >
              ✕ Réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* Collapsible panel */}
      {isOpen && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-4 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Row 1: Ville + Canton */}
          <div className="flex flex-wrap gap-6">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Ville
              </label>
              <input
                type="text"
                value={citySearch}
                onChange={e => setCitySearch(e.target.value)}
                placeholder="Rechercher une ville..."
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {availableCantons.length > 0 && (
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Canton
                </label>
                <div className="flex flex-wrap gap-2">
                  {availableCantons.map(canton => (
                    <span
                      key={canton}
                      onClick={() => toggleCanton(canton)}
                      className={chipClass(selectedCantons.includes(canton))}
                    >
                      {canton}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Statut */}
          {availableStatuses.length > 1 && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Statut
              </label>
              <div className="flex flex-wrap gap-2">
                {availableStatuses.map(status => {
                  const active = selectedStatuses.includes(status);
                  return (
                    <span
                      key={status}
                      onClick={() => toggleStatus(status)}
                      className={`px-3 py-1 rounded-full text-sm font-medium cursor-pointer border transition-colors select-none ${
                        active
                          ? STATUS_CHIP_COLORS[status]
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-blue-400'
                      }`}
                    >
                      {getStatusLabel(status)}
                    </span>
                  );
                })}
              </div>
              {onToggleOffMarket && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <button
                    onClick={onToggleOffMarket}
                    className={`flex items-center gap-2 px-3 py-1.5 text-xs rounded-full border transition-colors ${
                      showOffMarket
                        ? 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700'
                        : 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600 hover:border-red-300'
                    }`}
                  >
                    <span>⊘</span>
                    <span>{showOffMarket ? 'Masquer les retirées' : 'Inclure les retirées'}</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Row 2: Prix + Pièces */}
          <div className="flex flex-wrap gap-6">
            {globalMax > 0 && (
              <div className="flex-1 min-w-[260px]">
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Prix de vente
                  <span className="ml-2 text-blue-600 dark:text-blue-400 normal-case font-normal">
                    {formatPrice(priceRange[0])} — {formatPrice(priceRange[1])}
                  </span>
                </label>
                <div className="relative h-5 flex items-center">
                  <div className="absolute w-full h-1 rounded bg-gray-200 dark:bg-gray-600" />
                  <div
                    className="absolute h-1 rounded bg-blue-600"
                    style={{
                      left: `${((priceRange[0] - globalMin) / (globalMax - globalMin)) * 100}%`,
                      right: `${((globalMax - priceRange[1]) / (globalMax - globalMin)) * 100}%`,
                    }}
                  />
                  <input
                    type="range"
                    min={globalMin} max={globalMax} step={10000}
                    value={priceRange[0]}
                    onChange={e => {
                      const val = Number(e.target.value);
                      if (val <= priceRange[1]) setPriceRange([val, priceRange[1]]);
                    }}
                    className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-600 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:border-solid"
                    style={{ zIndex: priceRange[0] > globalMax - (globalMax - globalMin) * 0.05 ? 5 : 3 }}
                  />
                  <input
                    type="range"
                    min={globalMin} max={globalMax} step={10000}
                    value={priceRange[1]}
                    onChange={e => {
                      const val = Number(e.target.value);
                      if (val >= priceRange[0]) setPriceRange([priceRange[0], val]);
                    }}
                    className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-600 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:border-solid"
                    style={{ zIndex: 4 }}
                  />
                </div>
              </div>
            )}

            {availableRooms.length > 0 && (
              <div className="flex-1 min-w-[260px]">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Pièces de
                    </label>
                    <div className="relative">
                      <select
                        value={roomsMin ?? ''}
                        onChange={e => setRoomsMin(e.target.value === '' ? null : Number(e.target.value))}
                        className={selectClass}
                      >
                        <option value="">indiff.</option>
                        {availableRooms.map(r => (
                          <option key={r} value={r} disabled={roomsMax !== null && r > roomsMax}>
                            {formatRooms(r)}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Pièces à
                    </label>
                    <div className="relative">
                      <select
                        value={roomsMax ?? ''}
                        onChange={e => setRoomsMax(e.target.value === '' ? null : Number(e.target.value))}
                        className={selectClass}
                      >
                        <option value="">indiff.</option>
                        {availableRooms.map(r => (
                          <option key={r} value={r} disabled={roomsMin !== null && r < roomsMin}>
                            {formatRooms(r)}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Caractéristiques (flags LLM) */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Caractéristiques
            </label>
            <div className="flex flex-wrap gap-2">
              {/* Locataire en place */}
              <button
                onClick={() => setFlagRented(flagRented === true ? null : true)}
                className={chipClass(flagRented === true)}
                title="Afficher uniquement les biens avec locataire en place"
              >
                <span className="mr-1">🏠</span> Loué
              </button>
              <button
                onClick={() => setFlagRented(flagRented === false ? null : false)}
                className={chipClass(flagRented === false)}
                title="Afficher uniquement les biens libres (non loués)"
              >
                <span className="mr-1">🏠</span> Libre
              </button>
              {/* Rénovation — signal unifié description + photo */}
              <button
                onClick={() => setFlagRenovationLevel(flagRenovationLevel === 'major' ? null : 'major')}
                className={chipClass(flagRenovationLevel === 'major')}
                title="Rénovation lourde (description et/ou photo)"
              >
                🔨 Rénov. lourde
              </button>
              <button
                onClick={() => setFlagRenovationLevel(flagRenovationLevel === 'minor' ? null : 'minor')}
                className={chipClass(flagRenovationLevel === 'minor')}
                title="Travaux mineurs (description et/ou photo)"
              >
                🪚 Travaux mineurs
              </button>
              <button
                onClick={() => setFlagRenovationLevel(flagRenovationLevel === 'none' ? null : 'none')}
                className={chipClass(flagRenovationLevel === 'none')}
                title="Bon état — aucun travaux signalé"
              >
                ✓ Sans travaux
              </button>
              {/* Usage mixte */}
              <button
                onClick={() => setFlagMixed(flagMixed === true ? null : true)}
                className={chipClass(flagMixed === true)}
                title="Afficher uniquement les biens à usage mixte/commercial"
              >
                <span className="mr-1">🏪</span> Usage mixte
              </button>
              {/* Adresse incomplète */}
              <button
                onClick={() => setFlagAddressIncomplete(!flagAddressIncomplete)}
                className={chipClass(flagAddressIncomplete)}
                title="Afficher uniquement les biens avec adresse incomplète"
              >
                <span className="mr-1">📍</span> Adresse incomplète
              </button>
              {/* Stratégie locative */}
              <button
                onClick={() => setFlagStrategyLocatif(flagStrategyLocatif === true ? null : true)}
                className={chipClass(flagStrategyLocatif === true)}
                title="Biens détectés comme stratégie locative"
              >
                <span className="mr-1">💰</span> Locatif
              </button>
              {/* Stratégie flip */}
              <button
                onClick={() => setFlagStrategyFlip(flagStrategyFlip === true ? null : true)}
                className={chipClass(flagStrategyFlip === true)}
                title="Biens détectés comme stratégie flip"
              >
                <span className="mr-1">🔄</span> Flip
              </button>
              {/* Viabilité flip calculée */}
              <button
                onClick={() => setFlagFlipViability(flagFlipViability === 'green' ? null : 'green')}
                className={chipClass(flagFlipViability === 'green')}
                title="Flip viable au prix affiché (prix demandé ≤ PMA)"
              >
                🟢 Flip viable
              </button>
              <button
                onClick={() => setFlagFlipViability(flagFlipViability === 'orange' ? null : 'orange')}
                className={chipClass(flagFlipViability === 'orange')}
                title="Négociation nécessaire mais réaliste (écart ≤ 20%)"
              >
                🟠 Négociable
              </button>
              <button
                onClick={() => setFlagFlipViability(flagFlipViability === 'red' ? null : 'red')}
                className={chipClass(flagFlipViability === 'red')}
                title="Flip irréaliste — écart trop important (> 20%)"
              >
                🔴 Irréaliste
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
